import React from 'react';
import {
  Divider, Select, Switch, Text,
} from '@mantine/core';
import CollapsibleListField from '../../../puck/fields/CollapsibleListField';
import IconPickerField from '../../../puck/fields/IconPickerField';
import MediaSourceField from '../../../puck/fields/MediaSourceField';
import TextBackgroundColorField from '../../../puck/fields/TextBackgroundColorField';
import { PuckColorFieldWithPalette } from '../../../puck/fields/createPuckColorField';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { resolveCellType } from './tableLayout';
import { createRow } from './tableData';
import { CELL_TYPE_OPTIONS, columnTitle, displayCellType } from './TableColumnsField';

const CELL_TYPE_OVERRIDE_OPTIONS = [
  { label: 'Comme la colonne', value: '' },
  ...CELL_TYPE_OPTIONS,
];

/** Titre replié : le texte de la première cellule. */
const rowTitle = (row, index) => String(row?.cells?.[0]?.value || '')
  .replace(/<[^>]+>/g, '')
  .trim() || `Ligne ${index + 1}`;

/**
 * Réglages d'une cellule — le **texte** ne s'écrit pas ici, il s'édite sur le
 * canvas. Ne restent que ce qu'aucune frappe ne saurait poser : le type, le
 * média, l'icône.
 */
function CellFields({ column, columnIndex, cell, updateCell }) {
  const type = resolveCellType(cell, column);

  return (
    <>
      <Text size="xs" fw={600} c="dimmed">{columnTitle(column, columnIndex)}</Text>
      <Select
        size="xs"
        label="Type"
        data={CELL_TYPE_OVERRIDE_OPTIONS}
        value={displayCellType(cell.cellType, cell.media) || ''}
        onChange={(cellType) => updateCell({ cellType: cellType || '' })}
        comboboxProps={{ withinPortal: true }}
      />
      {type === 'text' && (
        <Text size="xs" c="dimmed">Le texte s’écrit directement sur la page.</Text>
      )}
      {/* Une image se choisit comme partout ailleurs dans l'éditeur
          (téléversement ou lien), et non plus en collant une URL à la main.
          Les cellules du parser gardent leur URL nue dans `value` : le rendu
          la lit toujours, tant qu'aucun média n'a été posé. */}
      {(type === 'image' || type === 'media') && (
        <MediaSourceField
          value={cell.media}
          onChange={(media) => updateCell({ media })}
          fieldLabel="Image"
          mediaFit="contain"
          allowVideo={false}
        />
      )}
      {type === 'video' && (
        <MediaSourceField
          value={cell.media}
          onChange={(media) => updateCell({ media })}
          fieldLabel="Vidéo"
          mediaFit="contain"
        />
      )}
      {type === 'icon' && (
        <>
          <IconPickerField
            value={cell.icon || ''}
            onChange={(icon) => updateCell({ icon })}
          />
          <PuckColorFieldWithPalette
            label="Couleur de l’icône"
            value={cell.badgeColor || ''}
            onChange={(badgeColor) => updateCell({ badgeColor: badgeColor ?? '' })}
          />
        </>
      )}
    </>
  );
}

/**
 * Champ « Lignes ».
 *
 * Une ligne ouverte liste ses cellules **par nom de colonne** — la liste Puck
 * native n'affichait que « Item #0 », sans dire de quelle colonne il s'agissait.
 */
export default function TableRowsField({ value, onChange }) {
  const { itemProps } = useSelectedItemProps();
  const columns = Array.isArray(itemProps.columns) ? itemProps.columns : [];

  return (
    <CollapsibleListField
      value={value}
      onChange={onChange}
      addLabel="Ajouter une ligne"
      createItem={() => createRow(columns.length)}
      itemTitle={rowTitle}
      minItems={1}
      renderItem={(item, update) => {
        const cells = Array.isArray(item.cells) ? item.cells : [];
        const updateCell = (index, patch) => update({
          cells: columns.map((_, i) => (
            i === index ? { ...(cells[i] || {}), ...patch } : (cells[i] || {})
          )),
        });

        return (
          <>
            <Switch
              size="sm"
              label="Mettre cette ligne en avant"
              checked={!!item.highlight}
              onChange={(e) => update({ highlight: e.currentTarget.checked })}
            />
            <TextBackgroundColorField
              label="Couleur de fond de la ligne"
              value={item.backgroundColor || ''}
              onChange={(backgroundColor) => update({ backgroundColor })}
            />
            {columns.map((column, index) => (
              <React.Fragment key={column.key || index}>
                <Divider my="xs" />
                <CellFields
                  column={column}
                  columnIndex={index}
                  cell={cells[index] || {}}
                  updateCell={(patch) => updateCell(index, patch)}
                />
              </React.Fragment>
            ))}
          </>
        );
      }}
    />
  );
}
